import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DefaultButton } from "../../common/components/DefaultButton";
import { FloatingLabel } from "../../common/components/FloatingLabel";
import { Col, Row } from "../../common/components/Grid";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useNetwork from "../../common/hooks/networkHook";
import useCredentials from "../../common/hooks/credentialsHook";
import { removeAssistance, requestAssistance, useJobAssistance } from "../../reducers/jobAssistanceSlice";
import { useTypes } from "../../reducers/typeSlice";
import PropTypes from "prop-types";
import { Modal } from "../../common/utils/dialogUtlities";
import { useSpecializedTranslation } from "../../hooks/specializedTranslationHook";
import { checkEnabled, checkEnabledValue } from "../../utils/enableUtilities";
import { updateJob, useJob } from "../../reducers/jobSlice";
import { JobRequestContext } from "../JobRequestsPanel";
import { AssistanceLevelDropdown, AssistanceSiteDropdown, AssistanceTypeDropdown, AssistanceTypeGrid } from "./AssistanceDropdowns";

const GenericAssistance = ({ job, disabled, assistance, onAdd, onRemove }) => {
    const { t } = useSpecializedTranslation(job);
    const clientConfig = useClientConfig();
    const credentials = useCredentials();

    const siteRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["site", "level"], job), [clientConfig, job]);
    const levelRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["level"], job), [clientConfig, job]);

    const { site, level } = job;
    const types = useTypes();

    const multipleAssistance = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceMode, ["multiple"], job), [clientConfig, job]);
    const [selection, setSelection] = useState({ site, level, type: multipleAssistance ? [] : null });
    const [showDialog, setShowDialog] = useState(false);
    const [reason, setReason] = useState(null);


    useEffect(() => {
        if ((!clientConfig.enableSites || site === selection.site) && (!clientConfig.enableLevels || level === selection.level)) {
            if (multipleAssistance && selection.type.some((t) => assistance?.some((a) => a.type === t && (!clientConfig.enableSites || a.site === site) && (!clientConfig.enableLevels || a.level === level)))) {
                setSelection(({ type: currentType, ...props }) =>
                    ({ ...props, type: currentType.filter((t) => !assistance?.some((a) => a.type === t && (!clientConfig.enableSites || a.site === site) && (!clientConfig.enableLevels || a.level === level))) })
                );
            } else if (!multipleAssistance && assistance?.some((a) => a.type === selection.type && (!clientConfig.enableSites || a.site === site) && (!clientConfig.enableLevels || a.level === level))) {
                setSelection(({ ...props }) => ({ ...props, type: null}));
            }
        }
    }, [site, level, assistance, selection.site, selection.level, selection.type, clientConfig, job, multipleAssistance]);

    const isInvalidRequest = (clientConfig.enableSites && !siteRestricted && !selection.site) 
        || (clientConfig.enableLevels && !levelRestricted && !selection.level) 
        || !selection.type || selection.type.length < 1
        || (Array.isArray(selection.type) && selection.type.some(t => !types.some(dt => (!dt.type || dt.type === t) && (!clientConfig.enableSites || !dt.site || dt.site === selection.site) && (!clientConfig.enableLevels || !dt.level || dt.level === selection.level))))
        || (!Array.isArray(selection.type) && !types.some(t => (!t.type || t.type === selection.type) && (!clientConfig.enableSites || !t.site || t.site === selection.site) && (!clientConfig.enableLevels || !t.level || t.level === selection.level)))
        || (checkEnabled(clientConfig, clientConfig.enableAssistanceReason, job) && !reason);

    const reasonInput = checkEnabled(clientConfig, clientConfig.enableAssistanceReason, job) ? <Row><Col><TextInput value={reason} placeholder={t('reason')} live={true} onChange={setReason} required={true}></TextInput></Col></Row> : null;

    const buildAssistance = type => {
        const assistance = { type: type };
        if (selection.site) {
            assistance.site = selection.site;
        } else if (siteRestricted) {
            assistance.site = job.site;
        }
        if (selection.level) {
            assistance.level = selection.level;
        } else if (levelRestricted) {
            assistance.level = job.level;
        }
        if (reason) {
            assistance.reason = reason;
        }
        return assistance
    }

    const reset = () => {
        setSelection({ site, level, type: checkEnabledValue(clientConfig, clientConfig.assistanceMode, ['multiple'], job) ? [] : null });
        setReason(null);
        setShowDialog(false);
    }

    const handleSelectionUpdate = useCallback(delta => {
        setSelection(selection => ({ ...selection, ...delta}))
    }, []);
    
    const addButton = <DefaultButton kind="add" disabled={isInvalidRequest}
        onClick={() => {
            if (Array.isArray(selection.type)) {
                onAdd(selection.type.map(st => buildAssistance(st)));
            } else {
                onAdd([buildAssistance(selection.type)]);
            }
            reset();
        }}></DefaultButton>

    const cancelButton = <DefaultButton kind="cancel" onClick={reset}></DefaultButton>;

    const makeLabel = a => a.type + (clientConfig.enableSites && !siteRestricted && a.site !== site ? ' - ' + a.site : '') + (clientConfig.enableLevels && !levelRestricted && a.level !== level ? ' - ' + a.level : '')

    const siteDropdown = !siteRestricted && <Row>
        <Col>
            <AssistanceSiteDropdown {...selection} job={job} assistance={assistance} onChange={handleSelectionUpdate}></AssistanceSiteDropdown>
        </Col>
    </Row>
    const levelDropdown = !levelRestricted && <Row>
        <Col>
            <AssistanceLevelDropdown {...selection} job={job} assistance={assistance} onChange={handleSelectionUpdate}></AssistanceLevelDropdown>
        </Col>
    </Row>
    const typeDropdown = <Row>
        <Col>
            {multipleAssistance ? 
                <AssistanceTypeGrid {...selection} job={job} assistance={assistance} onChange={handleSelectionUpdate}></AssistanceTypeGrid>
                : <AssistanceTypeDropdown {...selection} job={job} assistance={assistance} onChange={handleSelectionUpdate}></AssistanceTypeDropdown>
            }
        </Col>
    </Row>

    let ctrl;
    if (disabled) {
        ctrl = null;
    } else if (checkEnabledValue(clientConfig, clientConfig.assistanceMode, ['direct'], job)) {
        ctrl = <Col xs="auto" style={{ display: 'relative' }}>
            {reasonInput}
            {siteDropdown}
            {levelDropdown}
            {typeDropdown}
            <Row>
                <Col className="text-right">
                    <FloatingLabel hideLabel={true}>
                        {addButton}
                    </FloatingLabel>
                </Col>
            </Row>
        </Col>;
    } else {
        ctrl = <DefaultButton kind="add" onClick={() => setShowDialog(true)}></DefaultButton>
    }

    const canRemove = a => (!clientConfig.enableSites || (!checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ['site', 'level'], job)) || credentials.access.hasFull(types.filter(t => !t.site || t.site === a.site))) && (!clientConfig.enableLevels || !checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ['level'], job) || credentials.access.hasFull(types.filter(t => (!clientConfig.enableSites || !t.site || t.site === a.site) && (!t.level || t.level === a.level))))


    return <>
    <Modal show={showDialog} size={multipleAssistance ? "lg" : undefined} scrollable={true}>
        <Modal.Header>
            <h4>{t('jobAssistance')}</h4>
        </Modal.Header>
        <Modal.Body className="pt-0">
            <div className="pb-2" style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 500 }}>
                {reasonInput}
                {siteDropdown}
                {levelDropdown}
            </div>
            {typeDropdown}
        </Modal.Body>
        <Modal.Footer>
            {addButton}
            {cancelButton}
        </Modal.Footer>
    </Modal>
    <Row>
        <Col className="tags">
            {assistance?.map(a => {
                const label = makeLabel(a);
                return <div key={label} className="d-inline-block tag my-1">{label} {disabled || !canRemove(a) ? null : <DefaultButton kind="delete" variant="outline-dark" className="px-1 py-0" hideLabel={true} size="sm" onClick={() => onRemove(a)}></DefaultButton>}</div>
            })}
        </Col>  
        { ctrl }
    </Row></>
};
GenericAssistance.propTypes = {
  job: PropTypes.object,
  disabled: PropTypes.bool,
  assistance: PropTypes.array,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
};

export const RequestJobAccessAssistance = ({ job, assistance, disabled }) => {
    const { requestData, onChange } = useContext(JobRequestContext);
    const clientConfig = useClientConfig();

    const handleChange = useCallback(delta => {
        onChange({assistance: delta})
    } , [onChange]);    

    if (disabled) {
        return null;
    }

    return (
        <Row>
            <Col xs style={{ position: "relative" }}>
                { clientConfig.enableSites && <Row>
                    <Col>
                        <AssistanceSiteDropdown {...requestData} assistance={assistance} job={job} onChange={handleChange}></AssistanceSiteDropdown>
                    </Col>
                </Row> }
                { clientConfig.enableLevels && <Row>
                    <Col>
                        <AssistanceLevelDropdown {...requestData} assistance={assistance} job={job} onChange={handleChange}></AssistanceLevelDropdown>
                    </Col>
                </Row> }
                <Row>
                    <Col>
                        <AssistanceTypeDropdown {...requestData} assistance={assistance} job={job} onChange={handleChange}></AssistanceTypeDropdown>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

RequestJobAccessAssistance.propTypes = {
  job: PropTypes.object,
  assistance: PropTypes.array,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export const DefaultJobAssistance = ({ site, level, type, disabled, value, onAdd, onRemove}) => {
    return <GenericAssistance job={{ site, level, type }} assistance={value} disabled={disabled} onAdd={onAdd} onRemove={onRemove}></GenericAssistance>
}

export const JobAssistance = ({ job: { jobid, site, level, type }, disabled }) => {
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const assistance = useJobAssistance(jobid);
    const job = useJob(jobid);

    
    const onAdd = v => {
        if (jobid) {
            requestAssistance(clientConfig, network, v, jobid);
        } else {
            updateJob(network, { assistance: [ ...(job.assistance ?? []), ...v ]});
        }
    }

    const onRemove = v => {
        if (jobid) {
            removeAssistance(clientConfig, network, { jobid, ...v });
        } else {
            updateJob(network, { assistance: job.assistance?.filter(a => a.type !== v.type || (clientConfig.enableSites && a.site !== v.site) || (clientConfig.enableSites && a.level !== v.level)) ?? [] })
        }
    }

    return <GenericAssistance job={{ site, level, type }} assistance={job.jobid ? assistance : job.assistance} disabled={disabled} onAdd={onAdd} onRemove={onRemove}></GenericAssistance>
}
JobAssistance.propTypes = {
    job: PropTypes.object,
    disabled: PropTypes.bool
}

export const ScheduledJobAssistance = ({ job, disabled, onChange }) => {
    const onAdd = v => onChange({ assistance: [...job.assistance, ...v]});

    const onRemove = v => onChange({ assistance: job.assistance.filter(a => a !== v) });

    return <GenericAssistance job={{ site: job.site, level: job.level, type: job.type }} assistance={job.assistance ?? []} disabled={disabled} onAdd={onAdd} onRemove={onRemove}></GenericAssistance>
}
ScheduledJobAssistance.propTypes = {
    job: PropTypes.object,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
}
